import React, { useEffect, useState } from "react";
import { useRecoilState } from "recoil";
import { developerAccessTokenState } from "../global-state";
import "react-toastify/dist/ReactToastify.css";
import { Button, Table, Space, Row, Col } from "antd";
import LoadingSpinner from "../common/loading";
import { SAND_CAT } from "../config";
import { useToast } from "../common/toast";

const WaitlistUsersView = () => {
  const [developerAccessToken, setDeveloperAccessToken] = useRecoilState(
    developerAccessTokenState,
  );
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState([]);
  const { successToast, errorToast } = useToast();
  const [spinButton, setSpinButton] = useState(null);

  // State for selected rows
  const [selectedRows, setSelectedRows] = useState([]);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);

  useEffect(() => {
    loadWaitlistUsers();
  }, []);

  const loadWaitlistUsers = async () => {
    setLoading(true);
    const statusUrl = `${SAND_CAT}/waitlist/list`;
    try {
      const response = await fetch(statusUrl, {
        method: "GET",
        headers: {
          AdminAuthorization: `Bearer ${developerAccessToken}`,
          "Content-Type": "application/json",
        },
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data);
      }
      console.log(data);

      // Add unique id and transform timestamp
      const usersWithId = data
        .map((user, index) => ({
          ...user,
          id: index + 1,
          key: index + 1,
          formattedTimestamp: new Date(user.timestamp).toLocaleString(),
        }))
        // Sort by timestamp (earliest first)
        .sort((a, b) => a.timestamp - b.timestamp);

      setUsers(usersWithId);
      setLoading(false);
    } catch (error) {
      errorToast("Error fetching waitlist users! Please retry.");
      console.log(error);
      setLoading(false);
    }
  };

  // Columns for the Table
  const columns = [
    {
      title: "Email",
      dataIndex: "userEmail",
      width: 300,
      sorter: (a, b) => a.userEmail.localeCompare(b.userEmail),
    },
    {
      title: "Joined At",
      dataIndex: "formattedTimestamp",
      width: 250,
      sorter: (a, b) => a.timestamp - b.timestamp,
    },
    {
      title: "Wait Status",
      dataIndex: "wait",
      width: 100,
      render: (value) => (value ? "Waiting" : "Processed"),
    },
    {
      title: "Enrollment Status",
      dataIndex: "enroll",
      width: 150,
      render: (value) => (value ? "Enrolled" : "Not Enrolled"),
    },
    {
      title: "GSI",
      dataIndex: "gsi",
      width: 100,
    },
  ];

  // Handle enrolling selected users
  const handleEnrollUsers = async () => {
    setSpinButton("enroll");

    // Get the actual user emails based on selected rows
    const selectedEmails = selectedRows
      .map(user => user.userEmail)
      .filter(email => email !== null);

    try {
      const response = await fetch(`${SAND_CAT}/waitlist/enroll`, {
        method: "POST",
        headers: {
          AdminAuthorization: `Bearer ${developerAccessToken}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ emails: selectedEmails }),
      });

      const jsonData = await response.json();

      if (!response.ok) {
        throw new Error(jsonData);
      }

      // Check for partial success
      if (jsonData.success) {
        successToast(`Successfully enrolled`);

        // Refresh the list
        loadWaitlistUsers();
      } else {
        errorToast("Error!");
      }
    } catch (error) {
      errorToast("Error enrolling users. Please retry.");
      console.error("Error enrolling users:", error);
    }

    setSpinButton(null);
  };

  // Handle selection change
  const handleSelectionChange = (selectedRowKeys, selectedRows) => {
    setSelectedRowKeys(selectedRowKeys);
    setSelectedRows(selectedRows);
  };

  return (
    <div style={{ flexGrow: 1 }}>
      {loading ? (
        <LoadingSpinner />
      ) : (
        <Space direction="vertical" size="middle" style={{ width: "100%" }}>
          <div style={{ marginBottom: 16 }}>
            <Button
              type="primary"
              onClick={handleEnrollUsers}
              disabled={spinButton !== null || selectedRows.length === 0}
              loading={spinButton === "enroll"}
            >
              Enroll Selected Users
            </Button>
          </div>
          <div style={{ height: "100%", width: "100%" }}>
            <Table
              dataSource={users}
              columns={columns}
              rowSelection={{
                selectedRowKeys,
                onChange: handleSelectionChange,
              }}
              pagination={{
                pageSize: 20,
                pageSizeOptions: ["20"],
                showSizeChanger: true,
              }}
              size="small"
              rowKey="id"
              scroll={{ x: "max-content", y: "calc(100vh - 280px)" }}
            />
          </div>
        </Space>
      )}
    </div>
  );
};

export default WaitlistUsersView;
